import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';

const VerifyEmailPage = () => {
  const { token: paramToken, email: emailParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [verificationState, setVerificationState] = useState('loading');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [token, setToken] = useState(null);

  // Function to extract token and email from different URL formats
  const extractParamsFromURL = () => {
    const params = { token: null, email: null };
    
    // Method 1: Try to get from useParams first (for normal routing)
    if (paramToken) {
      params.token = paramToken;
    }
    if (emailParam) {
      params.email = emailParam;
    }

    const pathname = location.pathname;
    const hash = location.hash;
    const search = location.search;

    // Method 2: Extract from pathname (e.g., /verify-email/token123/email@example.com)
    if (!params.token || !params.email) {
      const pathMatch = pathname.match(/\/verify-email\/([^\/]+)(?:\/([^\/]+))?/);
      if (pathMatch) {
        if (!params.token && pathMatch[1]) {
          params.token = decodeURIComponent(pathMatch[1]);
        }
        if (!params.email && pathMatch[2]) {
          params.email = decodeURIComponent(pathMatch[2]);
        }
      }
    }

    // Method 3: Extract from hash (e.g., #/verify-email/token123/email@example.com)
    if (hash && (!params.token || !params.email)) {
      const hashMatch = hash.match(/\/verify-email\/([^\/\?]+)(?:\/([^\/\?]+))?/);
      if (hashMatch) {
        if (!params.token && hashMatch[1]) {
          params.token = decodeURIComponent(hashMatch[1]);
        }
        if (!params.email && hashMatch[2]) {
          params.email = decodeURIComponent(hashMatch[2]);
        }
      }
    }

    // Method 4: Extract from query parameters (e.g., ?token=token123&email=email@example.com)
    if (search && (!params.token || !params.email)) {
      const urlParams = new URLSearchParams(search);
      if (!params.token) {
        const queryToken = urlParams.get('token');
        if (queryToken) params.token = queryToken;
      }
      if (!params.email) {
        const queryEmail = urlParams.get('email');
        if (queryEmail) params.email = queryEmail;
      }
    }

    // Method 5: Extract from hash query parameters (e.g., #/verify-email?token=token123&email=email@example.com)
    if (hash && hash.includes('?') && (!params.token || !params.email)) {
      const hashParts = hash.split('?');
      if (hashParts[1]) {
        const hashParams = new URLSearchParams(hashParts[1]);
        if (!params.token) {
          const hashToken = hashParams.get('token');
          if (hashToken) params.token = hashToken;
        }
        if (!params.email) {
          const hashEmail = hashParams.get('email');
          if (hashEmail) params.email = hashEmail;
        }
      }
    }

    // Method 6: Try to extract from combined parameter (e.g., token123:email@example.com)
    if (params.token && !params.email && params.token.includes(':')) {
      const parts = params.token.split(':');
      if (parts.length === 2) {
        params.token = parts[0];
        params.email = parts[1];
      }
    }

    return params;
  };

  // API service class - same approach as ResetPasswordPage
  class AuthService {
    constructor() {
      this.baseURL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';
    }

    async request(endpoint, options = {}) {
      const url = `${this.baseURL}${endpoint}`;
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      };

      try {
        const response = await fetch(url, config);
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong');
        }
        
        return data;
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    }

    async verifyEmail(token) {
      return this.request(`/auth/verify-email/${token}`, {
        method: 'GET',
      });
    }

    async resendVerification(email) {
      return this.request('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    }
  }

  const authService = new AuthService();

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Extract parameters and verify email on component mount
  useEffect(() => {
    const params = extractParamsFromURL();
    
    if (params.email) {
      setEmail(params.email);
    }
    
    if (params.token) {
      setToken(params.token);
      if (!verificationAttempted) {
        verifyEmailWithToken(params.token);
      }
    } else {
      setVerificationState('error');
      setMessage('Token verifikasi tidak ditemukan. Silakan coba lagi dari email yang Anda terima.');
    }
  }, [paramToken, emailParam, location.pathname, location.hash, location.search, verificationAttempted]);

  // Updated verifyEmail function with better handling
  const verifyEmailWithToken = async (verificationToken) => {
    // Prevent multiple verification attempts
    if (verificationAttempted) {
      return;
    }
    
    setVerificationAttempted(true);
    
    try {
      setVerificationState('loading');
      
      // console.log('Attempting to verify email with token:', verificationToken);
      
      const response = await authService.verifyEmail(verificationToken);
      
      // console.log('Verification response:', response);
      
      if (response.success) {
        setVerificationState('success');
        
        // Check if email was already verified or just got verified
        if (response.data?.isEmailVerified || response.isEmailVerified) {
          if (response.data?.alreadyVerified) {
            setMessage('Email Anda sudah diverifikasi sebelumnya. Anda dapat langsung login.');
            setTimeout(() => {
              navigate('/login');
            }, 3000);
          } else {
            // First time verification or successful verification
            setMessage(response.message || 'Email Anda berhasil diverifikasi! Akun Anda sekarang aktif.');
            setTimeout(() => {
              navigate('/login');
            }, 5000);
          }
        } else {
          // Fallback success message
          setMessage(response.message || 'Email berhasil diverifikasi!');
          setTimeout(() => {
            navigate('/login');
          }, 5000);
        }
      }
      
    } catch (error) {
      console.error('Email verification failed:', error);
      
      // Handle different types of errors
      const errorMessage = error.message?.toLowerCase() || '';
      
      if (errorMessage.includes('tidak valid') || 
          errorMessage.includes('kedaluwarsa') ||
          errorMessage.includes('expired') ||
          errorMessage.includes('invalid') ||
          errorMessage.includes('sudah kedaluwarsa')) {
        
        // Check if this might be because email was already verified
        if (errorMessage.includes('sudah kedaluwarsa') || errorMessage.includes('tidak valid')) {
          // Could be that email is already verified and token is consumed
          setVerificationState('success');
          setMessage('Email Anda mungkin sudah diverifikasi sebelumnya. Silakan coba login.');
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setVerificationState('expired');
          setMessage('Link verifikasi sudah kadaluarsa atau tidak valid. Silakan minta link verifikasi baru.');
        }
      } else if (errorMessage.includes('sudah diverifikasi') || 
                 errorMessage.includes('already verified')) {
        setVerificationState('success');
        setMessage('Email Anda sudah diverifikasi sebelumnya. Anda dapat langsung login.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setVerificationState('error');
        setMessage(error.message || 'Terjadi kesalahan saat memverifikasi email. Silakan coba lagi.');
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email.trim()) {
      setMessage('Email tidak ditemukan. Silakan masukkan email Anda.');
      return;
    }
    
    if (!canResend) return;

    try {
      setIsLoading(true);
      
      const response = await authService.resendVerification(email);
      
      if (response.success) {
        setMessage('Email verifikasi berhasil dikirim ulang. Silakan cek email Anda.');
        setCanResend(false);
        setCountdown(60);
      }
      
    } catch (error) {
      console.error('Resend verification failed:', error);
      setMessage(error.message || 'Terjadi kesalahan saat mengirim ulang email verifikasi. Silakan coba lagi.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleTryLogin = () => {
    navigate('/login');
  };

  // Loading state
  if (verificationState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="text-center mb-8">
            <img
              src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
              alt="Telnet Indonesia"
              className="w-42 h-32 object-contain mx-auto"
              style={{ maxWidth: '70%' }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <style>
        {`
          .card-gradient {
            background: #2d3748;
            border: 1px solid rgba(139, 92, 246, 0.3);
          }
          
          .input-field {
            background: rgba(31, 41, 55, 0.8);
            border: 1px solid rgba(75, 85, 99, 0.5);
            transition: all 0.3s ease;
          }
          
          .input-field:focus {
            border-color: rgba(139, 92, 246, 0.5);
            box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
            background: rgba(31, 41, 55, 1);
          }
          
          .btn-gradient {
            background: linear-gradient(135deg, #050c9dff, #2b0a64ff);
          }
          
          .btn-gradient:hover:not(:disabled) {
            background: linear-gradient(135deg, #2b0a64ff, #050c9dff);
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(139, 92, 246, 0.3);
          }
          
          .success-gradient {
            background: linear-gradient(135deg, #059669, #047857);
          }
          
          .error-gradient {
            background: linear-gradient(135deg, #dc2626, #b91c1c);
          }
          
          .spinner {
            width: 60px;
            height: 60px;
            border: 6px solid rgba(139, 92, 246, 0.2);
            border-top: 6px solid #8b5cf6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div className="flex items-center justify-center min-h-screen py-20">
        <div className="max-w-md mx-auto px-4 w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
              alt="Telnet Indonesia"
              className="w-42 h-32 object-contain mx-auto"
              style={{ maxWidth: '70%' }}
            />
          </div>

          {/* Success State */}
          {verificationState === 'success' && (
            <div className="card-gradient rounded-3xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 success-gradient rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Email Berhasil Diverifikasi!</h2>
              <p className="text-gray-300 mb-6">{message}</p>
              <div className="text-gray-400 text-sm mb-4">
                Anda akan dialihkan ke halaman login dalam 5 detik...
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleTryLogin}
                  className="w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300"
                >
                  Lanjut ke Login
                </button>
                <Link
                  to="/"
                  className="block w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-300 text-center"
                >
                  Kembali ke Beranda
                </Link>
              </div>
            </div>
          )}

          {/* Error State */}
          {verificationState === 'error' && (
            <div className="card-gradient rounded-3xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 error-gradient rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Verifikasi Gagal</h2>
              <p className="text-gray-300 mb-6">{message}</p>
              
              {/* Email input for resending */}
              <div className="space-y-4 mb-6">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200 text-left">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleEmailChange}
                    className="input-field w-full px-4 py-3 rounded-xl text-white placeholder-gray-400 focus:outline-none"
                    placeholder="Masukkan email Anda"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleResendVerification}
                  disabled={isLoading || !canResend || !email.trim()}
                  className="w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Mengirim...</span>
                    </div>
                  ) : !canResend ? (
                    `Kirim Ulang (${countdown}s)`
                  ) : (
                    'Kirim Ulang Email Verifikasi'
                  )}
                </button>
                <button
                  onClick={handleTryLogin}
                  className="w-full px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-xl transition-all duration-300"
                >
                  Coba Login (Mungkin Sudah Terverifikasi)
                </button>
                <Link
                  to="/register"
                  className="block w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-300 text-center"
                >
                  Daftar Ulang
                </Link>
                <Link
                  to="/login"
                  className="block w-full px-6 py-3 bg-transparent border border-gray-600 hover:border-gray-500 text-white font-semibold rounded-xl transition-all duration-300 text-center"
                >
                  Kembali ke Login
                </Link>
              </div>
            </div>
          )}

          {/* Expired Token State */}
          {verificationState === 'expired' && (
            <div className="card-gradient rounded-3xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-yellow-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Link Kadaluarsa</h2>
              <p className="text-gray-300 mb-6">{message}</p>
              
              {/* Email input for resending */}
              <div className="space-y-4 mb-6">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200 text-left">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleEmailChange}
                    className="input-field w-full px-4 py-3 rounded-xl text-white placeholder-gray-400 focus:outline-none"
                    placeholder="Masukkan email Anda"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleResendVerification}
                  disabled={isLoading || !canResend || !email.trim()}
                  className="w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Mengirim...</span>
                    </div>
                  ) : !canResend ? (
                    `Kirim Link Baru (${countdown}s)`
                  ) : (
                    'Kirim Link Verifikasi Baru'
                  )}
                </button>
                <button
                  onClick={handleTryLogin}
                  className="w-full px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-xl transition-all duration-300"
                >
                  Coba Login (Mungkin Sudah Terverifikasi)
                </button>
                <Link
                  to="/login"
                  className="block w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-300 text-center"
                >
                  Kembali ke Login
                </Link>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 text-center">
            <div className="card-gradient rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Butuh bantuan?</h3>
              <p className="text-gray-300 text-sm mb-4">
                Jika Anda mengalami masalah dengan verifikasi email, silakan hubungi tim customer service kami.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center space-x-2 text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>support@telnet.co.id</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>0800-1234-5678</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;